import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  Globe, FileText, Zap, Rss, CheckSquare, Sparkles, Plus, ArrowRight,
  TrendingUp, Clock, LayoutDashboard, Code, Activity, Repeat, Layers,
  Tag, ShoppingCart, Share2, Smartphone, MessageSquare, MessageCircle,
  Plug, Puzzle, CreditCard, RefreshCw, CircleDot, XCircle, Send,
  ListTodo, ChevronRight, Star, ShoppingBag, DollarSign, Wallet
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider,
} from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache w localStorage z TTL
const CACHE_KEY = "epidemos_dashboard_cache";
const CACHE_TTL = 60000; // 60 sekund

function getCache() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL) return null;
    return data;
  } catch { return null; }
}

function setCache(data) {
  localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
}

// Mapa ikon modulow
const moduleIcons = {
  "layout-dashboard": LayoutDashboard, "code": Code, "globe": Globe,
  "activity": Activity, "trending-up": TrendingUp, "rss": Rss,
  "list": ListTodo, "repeat": Repeat, "layers": Layers, "tag": Tag,
  "shopping-cart": ShoppingCart, "share-2": Share2, "smartphone": Smartphone,
  "message-square": MessageSquare, "message-circle": MessageCircle,
  "check-square": CheckSquare, "plug": Plug, "puzzle": Puzzle,
  "credit-card": CreditCard,
};

export default function Dashboard() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [userStats, setUserStats] = useState(null);

  const role = user?.role || "advertiser";
  const isAdmin = role === "admin";

  const fetchData = useCallback(async (force = false) => {
    if (!force) {
      const cached = getCache();
      if (cached) {
        setData(cached.dashboard);
        setActivity(cached.activity);
        setUserStats(cached.userStats);
        setLastUpdate(cached.updatedAt);
        setLoading(false);
        return;
      }
    }
    setRefreshing(true);
    try {
      // Pobierz dane w zaleznosci od roli
      const requests = [
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/activity`),
      ];
      
      // Dla nie-adminow pobierz dane specyficzne dla uzytkownika
      if (!isAdmin) {
        requests.push(axios.get(`${API}/user/stats`).catch(() => ({ data: null })));
      }
      
      const results = await Promise.all(requests);
      const now = new Date().toISOString();
      
      setData(results[0].data);
      setActivity(results[1].data);
      if (!isAdmin && results[2]) {
        setUserStats(results[2].data);
      }
      setLastUpdate(now);
      setCache({ 
        dashboard: results[0].data, 
        activity: results[1].data, 
        userStats: !isAdmin && results[2] ? results[2].data : null,
        updatedAt: now 
      });
    } catch (err) {
      console.error("Blad pobierania danych:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusBadgeClass = (status) => ({
    completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    published: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    draft: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
    pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    running: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    todo: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
    in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    done: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    planned: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500",
  }[status] || "bg-slate-100 text-slate-600");

  const jobTypeLabel = (type) => ({
    generate: "Generowanie",
    rewrite: "Przepisanie",
    seo_analyze: "Analiza SEO",
    mass_generate: "Masowa generacja",
  }[type] || type);

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Rozne szybkie akcje w zaleznosci od roli
  const quickActions = isAdmin ? [
    { label: t("dashboard.addSite"), icon: Globe, path: "/sites", variant: "outline" },
    { label: t("dashboard.generateArticle"), icon: Sparkles, path: "/generator", variant: "default" },
    { label: t("dashboard.addFeed"), icon: Rss, path: "/rss", variant: "outline" },
    { label: t("dashboard.createTask"), icon: Plus, path: "/tasks", variant: "outline" },
  ] : role === "publisher" ? [
    { label: "Moje strony", icon: Globe, path: "/publisher", variant: "default" },
    { label: t("dashboard.generateArticle"), icon: Sparkles, path: "/generator", variant: "outline" },
    { label: "Portfel Zorki", icon: Star, path: "/zorki", variant: "outline" },
    { label: "Wiadomosci", icon: Send, path: "/messages", variant: "outline" },
  ] : [
    { label: "Przegladaj oferty", icon: ShoppingBag, path: "/shop", variant: "default" },
    { label: "Moje zamowienia", icon: FileText, path: "/my-orders", variant: "outline" },
    { label: "Portfel Zorki", icon: Star, path: "/zorki", variant: "outline" },
    { label: "Wiadomosci", icon: Send, path: "/messages", variant: "outline" },
  ];

  const totalAiUsage = (data.ai_usage.gpt || 0) + (data.ai_usage.claude || 0) + (data.ai_usage.gemini || 0);

  return (
    <TooltipProvider delayDuration={0}>
      <div data-testid="dashboard-page" className="space-y-6">
        {/* Naglowek z odswiezaniem */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">
              {t("dashboard.title")}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 font-body">
              {t("dashboard.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdate && (
              <span className="text-[10px] text-muted-foreground/60">
                {t("dashboard.lastUpdate")}: {new Date(lastUpdate).toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              data-testid="refresh-btn"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="gap-2 rounded-lg"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
              {t("dashboard.refreshData")}
            </Button>
          </div>
        </div>

        {/* ===== WIERSZ 1: Glowne statystyki - Bento Grid ===== */}
        {/* Statystyki dla Admina */}
        {isAdmin && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Strony */}
            <Card data-testid="stat-sites" className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <Globe className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                  <Badge variant="secondary" className="text-[10px] rounded-md">{data.sites.active} {language === "pl" ? "aktywnych" : "active"}</Badge>
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{data.sites.total}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">{t("dashboard.sites")}</p>
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
                  <span className="text-[11px] text-muted-foreground">
                    <span className="font-semibold text-foreground">{data.sites.wordpress}</span> WP
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    <span className="font-semibold text-foreground">{data.sites.html}</span> HTML
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Artykuly */}
            <Card data-testid="stat-articles" className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <FileText className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{data.articles.total}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">{t("dashboard.totalArticles")}</p>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border flex-wrap">
                  <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] border-0 rounded-md">
                    {data.articles.published} {t("dashboard.published")}
                  </Badge>
                  <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-[10px] border-0 rounded-md">
                    {data.articles.draft} {t("dashboard.drafts")}
                  </Badge>
                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] border-0 rounded-md">
                    {data.articles.pending} {t("dashboard.pending")}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Zadania AI */}
            <Card data-testid="stat-jobs" className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <Zap className="w-5 h-5 text-violet-500" strokeWidth={1.5} />
                  {data.jobs.running > 0 && (
                    <span className="flex items-center gap-1 text-[10px] text-blue-500 font-medium">
                      <CircleDot className="w-3 h-3 animate-pulse" /> {data.jobs.running}
                    </span>
                  )}
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{data.jobs.total}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">{t("dashboard.totalJobs")}</p>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border flex-wrap">
                  <span className="text-[11px] text-emerald-600 dark:text-emerald-400">
                    {data.jobs.completed} {t("dashboard.completedJobs")}
                  </span>
                  {data.jobs.failed > 0 && (
                    <span className="text-[11px] text-red-500">
                      {data.jobs.failed} {t("dashboard.failedJobs")}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Zadania & RSS */}
            <Card data-testid="stat-tasks-rss" className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <CheckSquare className="w-5 h-5 text-rose-500" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{data.tasks.total}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">{t("tasks.title")}</p>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border flex-wrap">
                  <span className="text-[11px] text-muted-foreground">
                    {data.tasks.todo} {t("tasks.statuses.todo")}
                  </span>
                  <span className="text-[11px] text-blue-500">
                    {data.tasks.in_progress} {t("dashboard.tasksInProgress")}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    <Rss className="w-3 h-3 inline mr-0.5" /> {data.rss.feeds}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Statystyki dla Wydawcy */}
        {role === "publisher" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <Globe className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{userStats?.sites_count || 0}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">Moje strony</p>
              </CardContent>
            </Card>

            <Card className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <FileText className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{userStats?.orders_count || 0}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">Zamowienia</p>
              </CardContent>
            </Card>

            <Card className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <Star className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{userStats?.zorki_balance || 0}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">Zorki</p>
              </CardContent>
            </Card>

            <Card className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <DollarSign className="w-5 h-5 text-green-500" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{userStats?.earnings || 0} PLN</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">Zarobki</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Statystyki dla Reklamodawcy */}
        {role === "advertiser" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <ShoppingBag className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{userStats?.orders_count || 0}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">Moje zamowienia</p>
              </CardContent>
            </Card>

            <Card className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <FileText className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{userStats?.articles_published || 0}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">Opublikowane</p>
              </CardContent>
            </Card>

            <Card className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <Star className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{userStats?.zorki_balance || 0}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">Zorki</p>
              </CardContent>
            </Card>

            <Card className="rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 bg-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <Wallet className="w-5 h-5 text-violet-500" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold font-heading text-foreground">{userStats?.credit_limit || 0}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body">Limit kredytowy</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== WIERSZ 2: AI Usage + Subskrypcja (tylko Admin) ===== */}
        {isAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* AI Usage - kompaktowe */}
            <Card data-testid="ai-usage-card" className="md:col-span-3 rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-heading flex items-center gap-2 text-foreground">
                  <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  {t("dashboard.aiUsage")}
                  <Badge variant="secondary" className="text-[10px] rounded-md ml-auto">{totalAiUsage} total</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4 pt-1">
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { name: "GPT-5.2", key: "gpt", color: "bg-emerald-500", bgLight: "bg-emerald-100 dark:bg-emerald-900/20" },
                    { name: "Claude", key: "claude", color: "bg-violet-500", bgLight: "bg-violet-100 dark:bg-violet-900/20" },
                    { name: "Gemini", key: "gemini", color: "bg-blue-500", bgLight: "bg-blue-100 dark:bg-blue-900/20" },
                  ].map((model) => (
                    <div key={model.key} className={`rounded-lg p-3 ${model.bgLight}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-foreground">{model.name}</span>
                        <span className="text-lg font-bold font-heading text-foreground">{data.ai_usage[model.key] || 0}</span>
                      </div>
                      <Progress
                        value={totalAiUsage > 0 ? ((data.ai_usage[model.key] || 0) / totalAiUsage) * 100 : 0}
                        className="h-1.5"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Subskrypcja */}
            <Card data-testid="subscription-card" className="rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-heading flex items-center gap-2 text-foreground">
                  <CreditCard className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  {t("dashboard.subscription")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4 pt-1">
                <Badge className="bg-primary/10 text-primary border-0 rounded-md text-xs font-bold mb-3">
                  {t(`dashboard.${data.subscription.plan}`)}
                </Badge>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{t("dashboard.sitesUsed")}</span>
                    <span className="font-semibold text-foreground">{data.sites.total}/{data.subscription.sites_limit}</span>
                  </div>
                  <Progress value={(data.sites.total / data.subscription.sites_limit) * 100} className="h-1.5" />
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{t("dashboard.postsUsed")}</span>
                    <span className="font-semibold text-foreground">{data.articles.total}/{data.subscription.posts_limit}</span>
                  </div>
                  <Progress value={(data.articles.total / data.subscription.posts_limit) * 100} className="h-1.5" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== WIERSZ 3: Recent Jobs + Quick Actions (tylko Admin widzi Recent Jobs) ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tabela Recent Jobs - tylko Admin */}
          {isAdmin && (
            <Card data-testid="recent-jobs-card" className="lg:col-span-2 rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2 pt-4 px-5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-heading flex items-center gap-2 text-foreground">
                    <Clock className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                    {t("dashboard.recentJobs")}
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-xs text-muted-foreground h-7 gap-1"
                    onClick={() => navigate("/generator")}
                  >
                    {t("dashboard.generateArticle")} <ChevronRight className="w-3 h-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-4 pt-1">
                {data.jobs.recent.length === 0 ? (
                  <div data-testid="no-jobs" className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                    <Zap className="w-8 h-8 mb-3 opacity-20" strokeWidth={1} />
                    <p className="text-xs">{t("dashboard.noJobs")}</p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-[260px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs font-semibold">{t("dashboard.jobType")}</TableHead>
                          <TableHead className="text-xs font-semibold">{t("dashboard.jobModel")}</TableHead>
                          <TableHead className="text-xs font-semibold">{t("dashboard.jobStatus")}</TableHead>
                          <TableHead className="text-xs font-semibold text-right">{t("dashboard.jobDate")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.jobs.recent.map((job, i) => (
                          <TableRow key={job.id || i} data-testid={`job-row-${i}`}>
                            <TableCell className="text-xs font-medium py-2.5">
                              {jobTypeLabel(job.job_type)}
                            </TableCell>
                            <TableCell className="py-2.5">
                              {job.params?.ai_model ? (
                                <Badge variant="outline" className="text-[10px] rounded-md">
                                  {job.params.ai_model.toUpperCase()}
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2.5">
                              <Badge className={`text-[10px] font-semibold border-0 rounded-md ${statusBadgeClass(job.status)}`}>
                                {job.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground text-right py-2.5">
                              {new Date(job.created_at).toLocaleDateString()}{" "}
                              {new Date(job.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Actions + Ostatnia aktywnosc */}
          <div className={`space-y-4 ${isAdmin ? "" : "lg:col-span-3"}`}>
            <Card className="rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-heading flex items-center gap-2 text-foreground">
                  <Zap className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  {t("dashboard.quickActions")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4 pt-1">
                <div className={`flex flex-col gap-1.5 ${isAdmin ? "" : "sm:flex-row sm:flex-wrap"}`}>
                  {quickActions.map((action, i) => (
                    <Button
                      key={i}
                      variant={action.variant}
                      data-testid={`quick-action-${i}`}
                      className={`justify-between h-9 rounded-lg text-xs font-medium ${isAdmin ? "" : "sm:flex-1 sm:min-w-[180px]"}`}
                      onClick={() => navigate(action.path)}
                    >
                      <span className="flex items-center gap-2">
                        <action.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {action.label}
                      </span>
                      <ArrowRight className="w-3 h-3 opacity-40" strokeWidth={1.5} />
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Mini aktywnosc */}
            <Card className="rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-heading flex items-center gap-2 text-foreground">
                  <Activity className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  {t("dashboard.recentActivity")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4 pt-1">
                {activity.length === 0 ? (
                  <p data-testid="no-activity" className="text-xs text-muted-foreground text-center py-4">
                    {t("dashboard.noActivity")}
                  </p>
                ) : (
                  <div className="space-y-1">
                    {activity.slice(0, 5).map((item, i) => (
                      <div key={i} data-testid={`activity-item-${i}`}
                        className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-accent/50 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">{item.title}</p>
                          <p className="text-[10px] text-muted-foreground">{item.module}</p>
                        </div>
                        <Badge className={`ml-2 shrink-0 text-[9px] font-semibold px-1.5 py-0 rounded-md border-0 ${statusBadgeClass(item.status)}`}>
                          {item.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ===== WIERSZ 4: Moduly systemu (tylko Admin) ===== */}
        {isAdmin && (
          <Card data-testid="system-modules-card" className="rounded-xl border shadow-sm bg-card">
            <CardHeader className="pb-2 pt-4 px-5">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading flex items-center gap-2 text-foreground">
                  <Layers className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  {t("dashboard.systemModules")}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] border-0 rounded-md">
                    {data.modules.filter(m => m.status === "active").length} {t("dashboard.moduleActive")}
                  </Badge>
                  <Badge className="bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500 text-[10px] border-0 rounded-md">
                    {data.modules.filter(m => m.status === "planned").length} {t("dashboard.modulePlanned")}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-5 pt-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                {data.modules.map((mod) => {
                  const IconComp = moduleIcons[mod.icon] || Layers;
                  const isActive = mod.status === "active";
                  return (
                    <Tooltip key={mod.id}>
                      <TooltipTrigger asChild>
                        <button
                          data-testid={`module-${mod.id}`}
                          onClick={() => isActive && mod.path !== "/" && navigate(mod.path)}
                          className={`flex items-center gap-2.5 p-3 rounded-lg border transition-all duration-200 text-left ${
                            isActive
                              ? "border-border hover:border-primary/40 hover:shadow-sm cursor-pointer bg-card"
                              : "border-border/50 opacity-50 cursor-default bg-muted/30"
                          }`}
                        >
                          <IconComp className={`w-4 h-4 shrink-0 ${isActive ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium truncate ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                              {mod.name}
                            </p>
                          </div>
                          {isActive && (
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs">
                        {mod.name} - {isActive ? t("dashboard.moduleActive") : t("dashboard.modulePlanned")}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center pb-4">
          <p className="text-[10px] text-muted-foreground/50 font-body">
            Epidemos v2.0 &middot; AI Content Management System
          </p>
        </div>
      </div>
    </TooltipProvider>
  );
}
